import { AuthScopes, ScheduleException } from '@Generated/back-office-client/src'
import { AlertDialog, Button, Dialog, Em, Flex, IconButton, Text } from '@radix-ui/themes'
import { useMutation } from '@tanstack/react-query'
import { Modal } from '@Components/common/modal'
import {
  formatDateToDayString,
  formatExceptionHoursToMatchClaimCenter,
  formatToTimeDisplay,
} from '@Components/util/utils'
import { parse, parseISO } from 'date-fns'
import { Cross2Icon, TrashIcon } from '@radix-ui/react-icons'
import { WithPermissions } from '@Components/common/with-permissions'
import { ClaimReservationsServiceAPI } from '@API/data-services/claim-reservations-service'

const TIME_FORMAT_STRING = 'HH:mm:ss'

export const ExceptionDetails = ({
  onOpenChange,
  claimCenterId,
  exception,
  refetchGrid,
  claimCenterStartHour,
  claimCenterEndHour,
}: {
  claimCenterId: string
  exception: ScheduleException
  claimCenterStartHour: number
  claimCenterEndHour: number
  onOpenChange: (open: boolean) => void
  refetchGrid: () => Promise<unknown>
}) => {
  const refetchGridAndCloseModal = () => {
    refetchGrid()
    onOpenChange(false)
  }

  const { mutateAsync: cancelException } = useMutation({
    mutationFn: async () => {
      return await ClaimReservationsServiceAPI.cancelException(claimCenterId, exception.id)
    },
    onSuccess: () => {
      refetchGridAndCloseModal()
    },
  })

  const startTime = parse(`${exception.localStartTime}`, TIME_FORMAT_STRING, new Date())
  const endTime = parse(`${exception.localEndTime}`, TIME_FORMAT_STRING, new Date())

  const { startTime: correctedStartTime, endTime: correctedEndTime } =
    formatExceptionHoursToMatchClaimCenter(
      startTime,
      endTime,
      claimCenterStartHour,
      claimCenterEndHour,
    )

  return (
    <Modal
      onOpenChange={onOpenChange}
      title="Exception Details"
      description={`Unavailable to book: ${exception.reason}`}
      hideCloseButton>
      <Flex gap="3" justify="end">
        <WithPermissions requiredPermissions={AuthScopes.writeclaim_center_exceptions}>
          <AlertDialog.Root>
            <AlertDialog.Trigger>
              <IconButton variant="ghost" aria-label="Cancel Reservation">
                <TrashIcon height="20" width="20" style={{ color: 'black' }} />
              </IconButton>
            </AlertDialog.Trigger>
            <AlertDialog.Content maxWidth="500px" style={{ paddingBottom: '60px' }}>
              <AlertDialog.Cancel>
                <Flex justify="end">
                  <IconButton variant="ghost" aria-label="Cancel">
                    <Cross2Icon height="20" width="20" style={{ color: 'black' }} />
                  </IconButton>
                </Flex>
              </AlertDialog.Cancel>
              <AlertDialog.Title align="center" style={{ paddingTop: '29px' }}>
                Are you sure you want to cancel?
              </AlertDialog.Title>
              <AlertDialog.Description size="2" style={{ paddingBottom: '18px' }} align="center">
                This action cannot be reversed.
              </AlertDialog.Description>

              <Flex gap="3" justify="center">
                <AlertDialog.Action onClick={() => cancelException()}>
                  <Button color="red">Cancel Unavailability</Button>
                </AlertDialog.Action>
              </Flex>
            </AlertDialog.Content>
          </AlertDialog.Root>
        </WithPermissions>

        <Dialog.Close>
          <IconButton variant="ghost" aria-label="Close">
            <Cross2Icon height="20" width="20" style={{ color: 'black' }} />
          </IconButton>
        </Dialog.Close>
      </Flex>

      <Flex gap="3" direction="column" style={{ padding: '18px 48px 48px 48px' }}>
        <Flex gap="3" align="center" style={{ justifyContent: 'center' }}>
          <h2>Unavailable to book</h2>
        </Flex>
        <Flex gap="3" align="center" style={{ justifyContent: 'center' }}>
          <h3>{formatDateToDayString(parseISO(exception.localDate))}</h3>
          <Text size="2">&#8729;</Text>
          <h3>
            {formatToTimeDisplay(correctedStartTime)}-{formatToTimeDisplay(correctedEndTime)}
          </h3>
        </Flex>
        <Flex gap="3" align="center" style={{ justifyContent: 'center' }}>
          <Text size="1">
            <Em>{exception.reason}</Em>
          </Text>
        </Flex>
      </Flex>
    </Modal>
  )
}
